/**
 * API utility functions for the Golf Course API
 */

export type User = {
  id: number;
  created_at: string;
  email: string;
  activated: boolean;
  subscription: string;
  price_cents?: number | null;
}

export type Location = {
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
};

export type Hole = {
  par?: number;
  yardage?: number;
  handicap?: number;
};

export type TeeBox = {
  tee_name?: string;
  course_rating?: number;
  slope_rating?: number;
  bogey_rating?: number;
  total_yards?: number;
  total_meters?: number;
  number_of_holes?: number;
  par_total?: number;
  front_course_rating?: number;
  front_slope_rating?: number;
  front_bogey_rating?: number;
  back_course_rating?: number;
  back_slope_rating?: number;
  back_bogey_rating?: number;
  holes: Hole[];
};

export type TeeBoxGrouping = {
  male?: TeeBox[];
  female?: TeeBox[];
};

export type GolfCourse = {
  id: number;
  club_name?: string;
  course_name: string;
  location: Location;
  tees: TeeBoxGrouping;
  scorecard_url?: string;
};

export type CourseUpdatePayload = {
  club_name?: string;
  course_name?: string;
  location?: Location;
  tees?: TeeBoxGrouping;
  scorecard_url?: string;
};

// The course endpoints branch on status codes (401 dead session, 403 wrong
// tier, 409 edit conflict, 422 validation), so their wrappers return the
// status alongside the parsed body instead of unwrapping like the wrappers
// below.
export type ApiResult<T> = {
  status: number;
  data?: T;
  error?: any;
};

// The only user allowed to see the course-update screen. This gate is UX
// only — the API still enforces a pro/enterprise session on every request.
export const ADMIN_EMAIL = "regan.c.loper@gmail.com";

// Base URL of the API. Overridable so local dev can point at a local backend
// (session cookies won't cross from localhost to the prod API host).
const API_BASE_URL =
  process.env.GATSBY_API_BASE_URL ?? "https://api.golfcourseapi.com";

// Stripe checkout is a plain HTML form POST (email travels in the body, no
// session cookie involved), so pages need the raw URL for the form action.
export const CHECKOUT_URL = `${API_BASE_URL}/create-checkout-session`;

// All /v1/frontend/* endpoints are gated by an Origin allowlist plus the
// shared X-Frontend-Key header, and authenticate the user via the httpOnly
// gcapi_session cookie — hence credentials: "include" on every call. The key
// is inlined into the client bundle at build (it isn't a true secret); it's
// defense-in-depth against non-browser clients and must match the backend's
// FRONTEND_SECRET.
async function frontendFetch(
  path: string,
  init: RequestInit = {}
): Promise<Response> {
  return fetch(`${API_BASE_URL}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "X-Frontend-Key": process.env.GATSBY_FRONTEND_SECRET ?? "",
      ...init.headers,
    },
  });
}

export function getDisplayPriceUsd(user: User | undefined, fallbackUsd: string): string {
  if (user?.price_cents != null) {
    return (user.price_cents / 100).toFixed(2);
  }
  return fallbackUsd;
}

/**
 * Fetches the signed-in user from the session cookie.
 * @returns The user, or null when there is no valid session (401)
 */
export async function getCurrentUser(): Promise<User | null> {
  const response = await frontendFetch("/v1/frontend/me");
  if (!response.ok) {
    return null;
  }
  const formatted = await response.json();
  return formatted.user ?? null;
}

/**
 * Asks the backend to email a sign-in magic link. Always succeeds (202)
 * whether or not the email has an account, to avoid enumeration.
 * @param email The user's email address
 * @returns Response with success or error
 */
export async function requestLogin(email: string) {
  const response = await frontendFetch("/v1/frontend/login-request", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
  return await response.json();
}

/**
 * Exchanges a sign-in magic-link token for a session. On 201 the backend
 * sets the httpOnly session cookie; the token is single-use.
 * @param token The sign-in token from the magic-link
 * @returns The signed-in user with response status (422 = invalid/expired)
 */
export async function createSession(token: string): Promise<ApiResult<User>> {
  const response = await frontendFetch("/v1/frontend/sessions", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
  const formatted = await response.json();
  if (!response.ok) {
    return { status: response.status, error: formatted?.error };
  }
  return { status: response.status, data: formatted.user };
}

/**
 * Logs out: deletes the current session server-side and clears the cookie.
 * Errors are swallowed — the caller always clears its local auth state.
 */
export async function deleteSession(): Promise<void> {
  try {
    await frontendFetch("/v1/frontend/sessions", { method: "DELETE" });
  } catch {
    // Logout must never fail from the user's perspective.
  }
}

/**
 * Rotates the signed-in user's API key in one step. The new key is returned
 * once in the response (the backend also emails it).
 * @returns The new API key with response status
 */
export async function rotateApiKey(): Promise<ApiResult<{ api_key: string }>> {
  const response = await frontendFetch("/v1/frontend/api-key/rotate", {
    method: "POST",
  });
  const formatted = await response.json();
  if (!response.ok) {
    return { status: response.status, error: formatted?.error };
  }
  return { status: response.status, data: formatted };
}

/**
 * Cancels the signed-in user's subscription (identity from the session).
 * @returns Cancellation response with status
 */
export async function cancelSubscription(): Promise<ApiResult<unknown>> {
  const response = await frontendFetch("/v1/frontend/subscription", {
    method: "DELETE",
  });
  const formatted = await response.json();
  if (!response.ok) {
    return { status: response.status, error: formatted?.error };
  }
  return { status: response.status, data: formatted };
}

/**
 * Creates a new user or sends a sign-in link. New emails get an activation
 * email; existing emails get a sign-in magic link instead.
 * @param email The user's email address
 * @returns Response with success or error
 */
export async function signIn(email: string) {
  const response = await fetch(`${API_BASE_URL}/v1/users`, {
    method: 'POST',
    body: JSON.stringify({
      email: email,
    }),
  });
  const formatted = await response.json();
  return formatted;
}

/**
 * Activates a user account with a token. The backend also sets the session
 * cookie on success, so the user lands signed in — credentials: "include"
 * is required for the browser to store that cross-origin Set-Cookie.
 * @param token The activation token
 * @returns Response with user data or error
 */
export async function activateAccount(token: string) {
  const response = await fetch(`${API_BASE_URL}/v1/users/activated`, {
    method: 'PUT',
    credentials: 'include',
    body: JSON.stringify({
      token,
    }),
  });
  const formatted = await response.json();
  return formatted;
}

/**
 * Searches for golf courses by name via the frontend-only endpoint. The
 * signed-in user comes from the session cookie (must be pro/enterprise).
 * @param query The search query
 * @param signal Aborts the request — used to cancel superseded typeahead calls
 * @returns Matching courses with response status
 */
export async function searchCourses(
  query: string,
  signal?: AbortSignal
): Promise<ApiResult<GolfCourse[]>> {
  const response = await frontendFetch(
    `/v1/frontend/search?search_query=${encodeURIComponent(
      query
    )}&fuzzy_match=true`,
    { signal }
  );
  const formatted = await response.json();
  if (!response.ok) {
    return { status: response.status, error: formatted?.error };
  }
  return { status: response.status, data: formatted.courses ?? [] };
}

/**
 * Fetches a single golf course by ID (session-authenticated)
 * @param id The course ID
 * @returns The course with response status
 */
export async function fetchCourse(id: number): Promise<ApiResult<GolfCourse>> {
  const response = await frontendFetch(`/v1/frontend/courses/${id}`);
  const formatted = await response.json();
  if (!response.ok) {
    return { status: response.status, error: formatted?.error };
  }
  return { status: response.status, data: formatted.course };
}

/**
 * Partially updates a golf course. Only the fields included in the payload
 * are changed; when `tees` is present it replaces the course's tee boxes
 * wholesale. Requires a pro/enterprise session.
 * @param id The course ID
 * @param payload The fields to update
 * @returns The updated course with response status
 */
export async function updateCourse(
  id: number,
  payload: CourseUpdatePayload
): Promise<ApiResult<GolfCourse>> {
  const response = await frontendFetch(`/v1/frontend/courses/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  const formatted = await response.json();
  if (!response.ok) {
    return { status: response.status, error: formatted?.error };
  }
  return { status: response.status, data: formatted.course };
}
